import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Filter, Star, MessageCircle, Calendar, Users, Award, Stethoscope, X } from 'lucide-react';
import { TablePagination, TextField, MenuItem, Select, FormControl, InputLabel, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import "./FeedbackPublic.css"; // Import the same CSS for custom scrollbar

export function FeedbackPrivate() {
  const [feedback, setFeedback] = useState([]);
  const [allFeedback, setAllFeedback] = useState([]);
  const [expandedComments, setExpandedComments] = useState({});
  const [stats, setStats] = useState({ averageRating: 0, totalResponses: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Filter states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    reviewerName: '',
    startDate: null,
    endDate: null,
    providerId: ''
  });
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    fetchFeedback();
    fetchProviders();
    
    // Set up real-time subscription for instant updates
    const subscription = supabase
      .channel('feedback_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'feedback' 
      }, () => {
        fetchFeedback(); // Refresh data when changes occur
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  useEffect(() => {
    applyFilters();
  }, [filters, allFeedback]);

  async function fetchProviders() {
    try {
      // Get unique providers from feedback data
      const { data: allFeedbackData, error: feedbackError } = await supabase
        .from('feedback')
        .select('provider_id, provider_name')
        .eq('is_public', false) // Only get providers from private feedback
        .not('provider_id', 'is', null);
        
      if (feedbackError) throw feedbackError;
      
      // Extract unique providers
      const uniqueProviders = [];
      const providerIds = new Set();
      
      if (allFeedbackData) {
        allFeedbackData.forEach(item => {
          if (item.provider_id && item.provider_name && !providerIds.has(item.provider_id)) {
            providerIds.add(item.provider_id);
            uniqueProviders.push({
              id: item.provider_id,
              name: item.provider_name
            });
          }
        });
      }
      
      setProviders(uniqueProviders);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  }

  async function fetchFeedback() {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('feedback')
        .select('*')
        .eq('is_public', false) // Only fetch private feedback
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      if (data) {
        setAllFeedback(data);
        applyFilters(data);
        
        // Calculate stats
        const totalResponses = data.length;
        let totalRating = 0;
        
        data.forEach(item => {
          totalRating += item.rating;
        });
        
        const averageRating = totalResponses > 0 ? (totalRating / totalResponses).toFixed(1) : 0;
        
        setStats({
          averageRating,
          totalResponses
        });
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  }
  
  const applyFilters = (feedbackData = allFeedback) => {
    let filteredData = [...feedbackData];
    
    // Filter by reviewer name
    if (filters.reviewerName) {
      filteredData = filteredData.filter(item => 
        item.name && item.name.toLowerCase().includes(filters.reviewerName.toLowerCase())
      );
    }
    
    // Filter by date range
    if (filters.startDate) {
      filteredData = filteredData.filter(item => 
        new Date(item.created_at) >= new Date(filters.startDate)
      );
    }
    
    if (filters.endDate) {
      filteredData = filteredData.filter(item => 
        new Date(item.created_at) <= new Date(filters.endDate)
      );
    }
    
    // Filter by provider
    if (filters.providerId) {
      filteredData = filteredData.filter(item => 
        item.provider_id === filters.providerId
      );
    }
    
    setFeedback(filteredData);
    // Reset pagination when filters change
    setPage(0);
  };
  
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleFilterDialogOpen = () => {
    setFilterDialogOpen(true);
  };
  
  const handleFilterDialogClose = () => {
    setFilterDialogOpen(false);
  };
  
  const handleResetFilters = () => {
    setFilters({
      reviewerName: '',
      startDate: null,
      endDate: null,
      providerId: ''
    });
    setFilterDialogOpen(false);
  };
  
  const handleApplyFilters = () => {
    applyFilters();
    setFilterDialogOpen(false);
  };

  const toggleExpand = (id) => {
    setExpandedComments(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const getCurrentPageData = () => {
    return feedback.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  // Get rating color based on value
  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-500';
  };

  // Function to get time ago
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMilliseconds = now - date;
    const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'today';
    if (diffInDays === 1) return 'left 1 day ago';
    return `left ${diffInDays} days ago`;
  };

  // Function to get initials from name
  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[parts.length-1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  // Function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Function to format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // We'll use an overlay for loading instead of replacing the entire UI

  if (loading === false && feedback.length === 0 && Object.keys(filters).every(key => 
    filters[key] === '' || filters[key] === null)) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow-md border p-5 text-gray-500">
        <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-3" />
        <h3 className="text-lg font-medium mb-1">No Feedback Yet</h3>
        <p>Feedback submitted by patients will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 h-screen flex flex-col relative">
      {/* Loading overlay that keeps UI visible */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex flex-col items-center justify-center z-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
          <p className="text-gray-600">Loading feedback...</p>
        </div>
      )}
      {/* Header with count and filter */}
      <div className="flex justify-between items-center mb-6 p-4 bg-white rounded-lg shadow-sm flex-shrink-0">
        <h2 className="text-xl font-medium text-gray-700">
          Private Feedback
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-sm">
            {feedback.length} feedback
          </span>
          <button 
            className="flex items-center gap-1 text-xs text-gray-600 border rounded-md px-2 py-1 hover:bg-gray-50"
            onClick={handleFilterDialogOpen}
          >
            <Filter size={14} /> Refine List
          </button>
        </div>
      </div>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 flex-shrink-0">
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 relative overflow-hidden">
          {/* Subtle background accent */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full opacity-50"></div>
          
          <div className="flex items-center relative z-10">
            <div className="bg-blue-50 p-3 rounded-full mr-4">
              <Star className="h-7 w-7 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-700">Average Rating</h3>
              <div className="flex items-center mt-1">
                <p className="text-3xl font-bold text-gray-800">{stats.averageRating}</p>
                <div className="flex ml-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star} 
                      size={16} 
                      className={`${star <= Math.round(stats.averageRating) ? 'fill-yellow-400' : 'text-gray-300'}`} 
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 relative overflow-hidden">
          {/* Subtle background accent */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-green-50 rounded-bl-full opacity-50"></div>
          
          <div className="flex items-center relative z-10">
            <div className="bg-green-50 p-3 rounded-full mr-4">
              <Users className="h-7 w-7 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-700">Total Responses</h3>
              <div className="flex items-center mt-1">
                <p className="text-3xl font-bold text-gray-800">{stats.totalResponses}</p>
                <span className="ml-2 text-sm text-gray-500 mt-1">
                  {stats.totalResponses > 0   }
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Container with page-level scroll */}
      <div className="rounded-lg shadow-sm p-4 mb-4 flex-grow overflow-auto flex flex-col">
        {/* Feedback list container */}
        <div className="space-y-5 pr-2 flex-grow">
          {getCurrentPageData().map((item) => (
            <div 
              key={item.id} 
              className={`bg-white rounded-lg shadow-sm border ${item.auto_generated ? 'border-green-100' : 'border-gray-200'} p-6 transition-shadow hover:shadow-md relative`}
            >
              {item.auto_generated && (
                <div className="absolute top-3 right-3">
                  <span className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full font-medium">Auto-generated</span>
                </div>
              )}
              
              {/* Header section with name, time, and provider */}
              <div className="flex items-start mb-4">
                {/* Avatar circle with initial */}
                <div className="flex-shrink-0 mr-4">
                  <div className={`w-12 h-12 rounded-full ${item.auto_generated ? 'bg-gradient-to-br from-green-50 to-green-100 text-green-700' : 'bg-gradient-to-br from-gray-50 to-blue-100 text-blue-700'} flex items-center justify-center font-semibold text-xl shadow-sm`}>
                    {getInitials(item.name || 'A')}
                  </div>
                </div>
                
                {/* Name and time ago */}
                <div className="flex-grow">
                  <div className="flex justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-800">
                        {item.name || 'Anonymous'}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {getTimeAgo(item.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <span className={`mr-1 font-medium ${getRatingColor(item.rating)}`}>{item.rating}</span>
                      <Star className={`h-5 w-5 ${getRatingColor(item.rating)} fill-current`} />
                    </div>
                  </div>
                  
                  {/* Provider and location info */}
                  {item.provider_name && (
                    <div className="mt-2 flex items-center text-sm text-gray-600 font-medium">
                      <Stethoscope className="h-4 w-4 mr-1 text-blue-500" />
                      {item.provider_name}
                    </div>
                  )}
                  
                  {/* Time and location - we'll show created_at for now */}
                  <div className="mt-1 flex items-center gap-1 text-gray-500">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">
                      {formatDate(item.created_at)} at {formatTime(item.created_at)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Comment content */}
              {item.comment && (
                <div className={`mt-3 p-4 rounded-lg border-l-4 ${item.rating >= 4 ? 'bg-green-50 border-green-300' : 'bg-blue-50 border-blue-300'}`}>
                  <p className="text-gray-700 leading-relaxed">
                    {expandedComments[item.id] 
                      ? item.comment 
                      : (item.comment.length > 150 
                          ? `${item.comment.substring(0, 150)}...` 
                          : item.comment)
                    }
                  </p>
                  {item.comment.length > 150 && (
                    <button 
                      onClick={() => toggleExpand(item.id)}
                      className="text-blue-600 hover:text-blue-800 mt-2 text-sm font-medium"
                    >
                      {expandedComments[item.id] ? 'Show Less' : 'Read More'}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          
          {feedback.length === 0 && Object.values(filters).some(value => value !== '' && value !== null) && (
            <div className="text-center py-12 bg-white rounded-lg shadow-md border p-5 text-gray-500">
              <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-medium mb-1">No Matching Feedback</h3>
              <p>No feedback matches your current filters.</p>
              <button 
                onClick={handleResetFilters}
                className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
        
        {/* Pagination component */}
        {feedback.length > 0 && (
          <div className="pt-3 border-t mt-4 bg-white">
            <TablePagination
              component="div"
              count={feedback.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Feedback per page:"
            />
          </div>
        )}
      </div>
      
      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={handleFilterDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex justify-between items-center">
          <span>Refine Private Feedback</span>
          <button onClick={handleFilterDialogClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </DialogTitle>
        <DialogContent dividers>
          <div className="space-y-4">
            {/* Reviewer Name Filter */}
            <div>
              <TextField
                label="Reviewer Name"
                fullWidth
                variant="outlined"
                value={filters.reviewerName}
                onChange={(e) => handleFilterChange('reviewerName', e.target.value)}
                placeholder="Search by name"
                size="small"
              />
            </div>
            
            {/* Date Range Filter */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-600">Feedback Date</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="From"
                    value={filters.startDate}
                    onChange={(date) => handleFilterChange('startDate', date)}
                    renderInput={(params) => <TextField {...params} size="small" />}
                    maxDate={filters.endDate || undefined}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="To"
                    value={filters.endDate}
                    onChange={(date) => handleFilterChange('endDate', date)}
                    renderInput={(params) => <TextField {...params} size="small" />}
                    minDate={filters.startDate || undefined}
                  />
                </LocalizationProvider>
              </div>
            </div>
            
            {/* Provider Filter */}
            <div>
              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={filters.providerId}
                  onChange={(e) => handleFilterChange('providerId', e.target.value)}
                  label="Provider"
                >
                  <MenuItem value="">
                    <em>Any Provider</em>
                  </MenuItem>
                  {providers.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        </DialogContent>
        <DialogActions className="bg-gray-50 p-4">
          <div className="flex justify-between w-full">
            <Button
              onClick={handleResetFilters}
              variant="outlined"
              color="inherit"
            >
              Reset to default
            </Button>
            <Button
              onClick={handleApplyFilters}
              variant="contained"
              color="primary"
            >
              Apply
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default FeedbackPrivate;